import _ from "underscore";
import { defineStore } from "pinia";

import { Panels } from "@/models/PanelEnums";
import { isThisMe } from "@/services/UtilitiesService";
import { caseFileService, metadataService } from "@/services";

import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { useEvidenceStore } from "@/stores/evidence-store";
import { useMetadataStore } from "@/stores/metadata-store";

import type { ApiError } from "@/services/Api";
import type { Metadata } from "@/models/Metadata";
import type { CachedFile } from "@/models/CachedFile";
import type VoiceMetadata from "@/models/VoiceMetadata";
import type ExtendedMetadata from "@/models/ExtendedMetadata";

export const useCaseFileStore = defineStore("caseFile", {
  state: (): State => ({
    metadataLoading: false,
    cachedFiles: [],
    currentPanelGlobal: {
      audio: Object.keys(Panels.audio).reduce((curr, next) => ({ ...curr, [next]: false }), {}),
      viewer: Object.keys(Panels.viewer).reduce((curr, next) => ({ ...curr, [next]: false }), {}),
      transcript: Object.keys(Panels.transcript).reduce((curr, next) => ({ ...curr, [next]: false }), {}),
      shared: Object.keys(Panels.shared).reduce((curr, next) => ({ ...curr, [next]: false }), {}),
    },
    cachedFilesData: {},
    selectedThemes: undefined,
  }),
  getters: {
    loading(): boolean {
      return this.metadataLoading;
    },
    selectedCaseFile(): string | undefined {
      return this.currentRoute.params.casefile as string | undefined;
    },
    currentFile(): Metadata | undefined {
      const selectedCaseFile = this.selectedCaseFile;
      if (!selectedCaseFile) return undefined;
      return useMetadataStore().files.find((f) => isThisMe(f, selectedCaseFile));
    },
    fileTranscript(): TranscriptStoreData | undefined {
      if (!this.selectedCaseFile) return undefined;
      return this.cachedFilesData[this.selectedCaseFile]?.transcript;
    },
    fileForm(): FormStoreData | undefined {
      if (!this.selectedCaseFile) return undefined;
      return this.cachedFilesData[this.selectedCaseFile]?.form;
    },
    fileMetadata(): MetadataStoreData | undefined {
      if (!this.selectedCaseFile) return undefined;
      return this.cachedFilesData[this.selectedCaseFile]?.metadata;
    },
    fileExtendedMetadata(): ExtendedMetadata | undefined {
      if (!this.selectedCaseFile) return undefined;
      return this.cachedFilesData[this.selectedCaseFile]?.extendedMetadata;
    },
    fileReferences(): Record<"voice" | "form", string | undefined> | undefined {
      if (!this.selectedCaseFile) return undefined;
      return this.cachedFilesReferences[this.selectedCaseFile];
    },
    formOnlyRecording(): boolean {
      if (!this.selectedCaseFile) {
        return false;
      }

      if (this.currentFile?.fileType !== "ac5") {
        return false;
      }

      const audioMetadata = this.cachedFilesData[this.selectedCaseFile]?.metadata?.data;

      if (!audioMetadata) {
        return false;
      }

      return audioMetadata.audio === undefined && audioMetadata.session !== undefined;
    },
    cachedFilesReferences(): Partial<Record<string, Record<"voice" | "form", string | undefined>>> {
      const result: Partial<Record<string, Record<"voice" | "form", string | undefined>>> = {};

      for (const fileReference of Object.keys(this.cachedFilesData)) {
        let voice: string | undefined;
        let form: string | undefined;

        const relatedParentFiles = useMetadataStore().files.filter((f) => isThisMe(f, fileReference, "parent"));

        {
          /* Check for voice files linked to the active case file */
          const voiceAudioFile = relatedParentFiles.find((f) => f.fileType === "audio");

          if (voiceAudioFile != null) voice = voiceAudioFile.fileReference;
          else {
            /* Fallback to casefile only if the file is an audio file */
            const rawFile = useMetadataStore().files.find((f) => isThisMe(f, fileReference));
            if (rawFile?.fileType === "audio") voice = rawFile.fileReference;
          }
        }

        {
          const rawFile = relatedParentFiles.find((f) => ["ac5", "form"].includes(f.fileType));
          form = rawFile?.fileReference;
        }

        result[fileReference] = { voice, form };
      }

      return result;
    },
  },
  actions: {
    async onSelectedFileChange(): Promise<void> {
      if (this.selectedCaseFile == null || this.currentFile == null) return;

      this.cachedFilesData[this.selectedCaseFile] ??= {};

      const queue: Array<Promise<unknown>> = [this.loadExtendedMetadata()];

      {
        if (this.currentFile.fileType === "ac5") queue.push(this.loadVoiceMetadata());
        else if (this.currentFile.fileType === "audio")
          this.setVoiceMetadata(this.selectedCaseFile, { status: "success", data: undefined });
        else this.setVoiceMetadata(this.selectedCaseFile, { status: "not-exists" });
      }

      {
        if (["audio", "ac5"].includes(this.currentFile.fileType)) queue.push(this.loadTranscript());
        else this.setTranscript(this.selectedCaseFile, { status: "not-exists" });
      }

      {
        if (["form", "ac5"].includes(this.currentFile.fileType)) queue.push(this.loadForm());
        else this.setForm(this.selectedCaseFile, { status: "not-exists" });
      }

      await Promise.all(queue);
    },
    async loadExtendedMetadata(): Promise<void> {
      const selectedCaseName = useCaseStore().selectedCaseName;
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null || selectedCaseName == null || this.currentFile == null)
        return;

      const extendedMetadata = await metadataService.getExtendedMetadata(
        selectedClient,
        selectedBrand,
        selectedCaseName,
        this.currentFile.original,
      );
      this.setExtendedMetadata(this.currentFile.fileReference, extendedMetadata);
    },
    async loadVoiceMetadata(): Promise<void> {
      const selectedCaseName = useCaseStore().selectedCaseName;
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null || selectedCaseName == null || this.selectedCaseFile == null)
        return;

      this.setVoiceMetadata(this.selectedCaseFile, { status: "loading" });

      try {
        const voiceMetadataUri = await caseFileService.get(
          selectedClient,
          selectedBrand,
          selectedCaseName,
          this.selectedCaseFile,
          true,
        );

        const voiceMetadata = (await fetch(voiceMetadataUri).then((r) => r.json())) as VoiceMetadata;
        this.setVoiceMetadata(this.selectedCaseFile, { status: "success", data: voiceMetadata });
      } catch (error) {
        console.error(error);
        this.setVoiceMetadata(this.selectedCaseFile, { status: "error" });
      }
    },
    async loadTranscript() {
      const selectedCaseName = useCaseStore().selectedCaseName;
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null || selectedCaseName == null || this.selectedCaseFile == null)
        return;

      const voiceFileReference = this.cachedFilesReferences[this.selectedCaseFile]?.voice;

      if (!voiceFileReference) {
        this.setTranscript(this.selectedCaseFile, { status: "not-exists" });
        return;
      }

      this.setTranscript(this.selectedCaseFile, { status: "loading" });

      try {
        try {
          const transcript = await caseFileService.getSupportingType(
            selectedClient,
            selectedBrand,
            selectedCaseName,
            voiceFileReference,
            "transcript",
          );

          this.setTranscript(this.selectedCaseFile, {
            status: "success",
            speakers: transcript.speakers ?? [],
            utterances: (transcript.utterances ?? transcript.transcript ?? []).map((utterance) => ({
              isContext: true,
              speaker: utterance.speaker,
              endTime: utterance.endTime,
              startTime: utterance.startTime,
              dialogueText: utterance.text ?? utterance.dialogueText ?? "",
            })),
          });
        } catch (e) {
          const err = e as ApiError;

          if (err.status !== 204 && err.status !== 404) throw err;
          else this.setTranscript(this.selectedCaseFile, { status: "not-exists" });
        }
      } catch (error) {
        console.error(error);
        this.setTranscript(this.selectedCaseFile, { status: "error" });
      }
    },
    async loadForm() {
      const selectedCaseName = useCaseStore().selectedCaseName;
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null || selectedCaseName == null || this.selectedCaseFile == null)
        return;

      const formFileReference = this.cachedFilesReferences[this.selectedCaseFile]?.form;

      if (!formFileReference) {
        this.setForm(this.selectedCaseFile, { status: "not-exists" });
        return;
      }

      this.setForm(this.selectedCaseFile, { status: "loading" });

      try {
        const answersURL = await caseFileService.get(
          selectedClient,
          selectedBrand,
          selectedCaseName,
          formFileReference,
          true,
        );

        const qpackAnswers = (await fetch(answersURL).then((r) => r.json())) as QPackAnswers;
        const qpackSchema = (await caseFileService.getFormDefinition(
          selectedClient,
          selectedBrand,
          "capture",
          qpackAnswers.uuid,
          qpackAnswers.version.toString(),
        )) as QPackSchema;

        this.setForm(this.selectedCaseFile, {
          status: "success",
          form: { schema: qpackSchema, answers: qpackAnswers },
        });
      } catch (error) {
        this.setForm(this.selectedCaseFile, { status: "error" });
      }
    },
    currentViewerPanelGlobalChanged(key: string, state: boolean) {
      this.setCurrentPanelGlobalChanged("viewer", key, state);
    },
    currentTranscriptPanelGlobalChanged(key: string, state: boolean) {
      this.setCurrentPanelGlobalChanged("transcript", key, state);
    },
    addIntoCache(payload: { fileReference: string; path: string }): void {
      const fileCache = this.cachedFiles.find((f) => f.fileReference === payload.fileReference);

      if (fileCache != null) fileCache.lastOpened = Date.now();
      else {
        const newCache = [...this.cachedFiles, { ...payload, lastOpened: Date.now() }];

        if (newCache.length > 20) {
          // Remove from list
          newCache.splice(
            // Get item index
            newCache.indexOf(
              // Get oldest opened cache
              newCache.reduce((acc, cur) => (cur.lastOpened < acc.lastOpened ? cur : acc)),
            ),
            1,
          );
        }

        this.cachedFiles = newCache;
      }
    },

    // Mutations
    setCurrentPanelGlobalChanged(key: "transcript" | "viewer", panel: string, state: boolean) {
      if (panel in Panels.shared) {
        this.currentPanelGlobal.shared = {
          ...this.currentPanelGlobal.shared,
          [panel]: state,
        };

        return;
      }

      this.currentPanelGlobal[key] = {
        ...this.currentPanelGlobal[key],
        [panel]: state,
      };
    },
    setMetadataLoading(payload: { isLoading: boolean }): void {
      this.metadataLoading = payload.isLoading;
    },
    setExtendedMetadata(casefile: string, payload: ExtendedMetadata): void {
      this.cachedFilesData[casefile] ??= {};

      this.cachedFilesData[casefile].extendedMetadata = {
        ...payload,
        officeProperties: _.isString(payload.officeProperties)
          ? (JSON.parse(decodeURIComponent(payload.officeProperties.replace(/\+/g, "%20"))) as object)
          : {},
        officeCustomProperties: _.isString(payload.officeCustomProperties)
          ? (JSON.parse(decodeURIComponent(payload.officeCustomProperties.replace(/\+/g, "%20"))) as object)
          : {},
      };
    },
    removeItemFromCache(payload: { fileReference: string }): void {
      const itemIndex = this.cachedFiles.findIndex((f) => f.fileReference === payload.fileReference);
      if (itemIndex < 0) return;
      this.cachedFiles.splice(itemIndex, 1);
      this.cachedFilesData[payload.fileReference] = undefined;
    },
    reset() {
      this.$reset();
    },
    setVoiceMetadata(
      casefile: string,
      payload: { status: "loading" | "error" | "not-exists" } | (MetadataStoreData & { status: "success" }),
    ): void {
      this.cachedFilesData[casefile] ??= {};

      if (payload.status === "success") this.cachedFilesData[casefile].metadata = payload;
      else this.cachedFilesData[casefile].metadata = { ...payload, data: undefined };
    },
    setTranscript(
      casefile: string,
      payload: { status: "loading" | "error" | "not-exists" } | ({ status: "success" } & TranscriptStoreData),
    ): void {
      this.cachedFilesData[casefile] ??= {};

      if (payload.status === "success") this.cachedFilesData[casefile].transcript = payload;
      else this.cachedFilesData[casefile].transcript = { ...payload, utterances: [], speakers: [] };
    },
    setForm(
      casefile: string,
      payload: { status: "loading" | "error" | "not-exists" } | ({ status: "success" } & FormStoreData),
    ): void {
      this.cachedFilesData[casefile] ??= {};

      if (payload.status === "success") this.cachedFilesData[casefile].form = payload;
      else this.cachedFilesData[casefile].form = { ...payload, form: undefined };
    },
    setCommittedRedactionsState(casefile: string, hasCommittedRedactions: boolean): void {
      this.cachedFilesData[casefile] ??= {};
      this.cachedFilesData[casefile].hasCommittedRedactions = hasCommittedRedactions;
    },
    setFormAnswers(casefile: string, newAnswers: QPackAnswers) {
      this.cachedFilesData[casefile] ??= {};

      if (this.cachedFilesData[casefile].form?.form == null || this.cachedFilesData[casefile].form.status !== "success")
        return;

      this.cachedFilesData[casefile].form.form.answers = newAnswers;
    },
    setSelectedThemes(payload: string[] | string | boolean | undefined) {
      this.selectedThemes = (() => {
        if (payload == null || Array.isArray(payload)) return payload;

        // Select/Deselect all items
        if (typeof payload === "boolean") return payload ? undefined : [];

        const categories = useEvidenceStore().availableCategoriesWithEvidences;

        // Currently all items are selected, so filter out the item that triggered this to toggle it
        if (this.selectedThemes == null) return categories.filter((c) => c.name !== payload).map((c) => c.name);

        // The category is selected, so we should remove it
        if (this.selectedThemes.includes(payload)) return this.selectedThemes.filter((column) => column !== payload);

        // This item isn't selected, add it.
        return (
          // If the selected list needs one more item to match the categories available,
          this.selectedThemes.length === categories.length - 1
            ? // then selecting this category would mark all items as 'selected'
              undefined
            : // otherwise can singly add this category
              [...this.selectedThemes, payload]
        );
      })();
    },
  },
});

interface State {
  metadataLoading: boolean;
  cachedFiles: CachedFile[];
  currentPanelGlobal: Record<"viewer" | "transcript" | "audio" | "shared", Record<string, boolean>>;
  cachedFilesData: {
    [casefile: string]:
      | Partial<{
          form: FormStoreData;
          metadata: MetadataStoreData;
          transcript: TranscriptStoreData;
          extendedMetadata: ExtendedMetadata;
          hasCommittedRedactions: boolean;
        }>
      | undefined;
  };
  /**
   * When undefined, all themes are selected
   * When empty, no themes are selected
   * When non-empty, only the listed are selected
   */
  selectedThemes: string[] | undefined;
}

interface MetadataStoreData {
  status: "loading" | "success" | "error" | "not-exists";
  data: VoiceMetadata | undefined;
}

interface TranscriptStoreData {
  status: "loading" | "success" | "error" | "not-exists";
  speakers: number[];
  utterances: Utterance[];
}

interface FormStoreData {
  status: "loading" | "success" | "error" | "not-exists";
  form: QPackForm | undefined;
}

export interface Utterance {
  isContext: boolean;
  speaker: string | number;
  startTime: number;
  endTime: number;
  dialogueText: string;
}

interface TranscriptMetadata {
  source: {
    fileReference: string;
    brand: string;
    caseReference: string;
  };
}

export interface Transcript {
  speakers: number[] | undefined;
  /** @deprecated Prefer {@link Transcript["utterances"]} */
  transcript?: RawUtterance[];
  utterances: RawUtterance[] | undefined;
  metadata: TranscriptMetadata | undefined;
}

interface RawUtterance {
  uuid: string;
  order: number;
  startWord: number;
  endWord: number;
  text?: string;
  dialogueText: string | undefined;
  confidence?: number;
  endTime: number;
  speaker: number | string;
  startTime: number;
}

export interface QPackForm {
  schema: QPackSchema;
  answers: QPackAnswers;
}

interface QPackAnswers {
  uuid: string;
  version: number;
  __editEvents?: FieldEditBookmark[];
  [ID: string]: unknown;
}

interface QPackSchema {
  uuid: string;
  version: number;
  name: string;
  fields: object;
  status: string;
}

interface FieldEditBookmark {
  path: string;
  startTime: number;
  startTimestamp: string;
  endTime: number;
  endTimestamp: string;
}
